import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tag, Trash2, Copy, Users, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AdminDiscountCodes() {
  const { user, loading } = useAuth();
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/discount-codes");
      setCodes(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Admin only");
      else { console.error("Load failed", e); toast.error("Could not load — please refresh"); }
    }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace/>;
  if (!user.is_admin) return <Navigate to="/dashboard" replace/>;

  const create = async () => {
    if (!code.trim() || code.trim().length < 3) { toast.error("Code must be at least 3 characters"); return; }
    if (!pct || pct < 1 || pct > 100) { toast.error("Percent must be between 1 and 100"); return; }
    setBusy(true);
    try {
      const payload = { code: code.trim().toUpperCase(), percent_off: parseInt(pct), note: note || null };
      if (maxUses) payload.max_uses = parseInt(maxUses);
      await api.post("/admin/discount-codes", payload);
      toast.success(`Code ${payload.code} created`);
      setCode(""); setPct(10); setMaxUses(""); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create code");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete code "${c}"? Existing orders that used it keep their discount.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${c}`);
      toast.success(`Code ${c} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const copy = (c) => {
    navigator.clipboard?.writeText(c);
    toast.success(`${c} copied to clipboard`);
  };

  return (
    <div data-testid="admin-codes-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Studio Console</div>
          <h1 className="font-serif text-4xl mt-2" style={{color: "var(--primary)"}}>Discount Codes</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-xl">Create codes for friends, family, or seasonal promos. Customers apply them during quote review. They stack with the Military/First Responder discount.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/orders"><Button variant="outline" size="sm" className="rounded-full">← Orders</Button></Link>
          <Link to="/admin/inbox"><Button variant="outline" size="sm" className="rounded-full">Inbox</Button></Link>
        </div>
      </div>

      {/* Create */}
      <div data-testid="create-code-form" className="rounded-lg border hairline bg-white p-6 mb-10">
        <div className="uppercase text-[11px] tracking-[0.3em] mb-3" style={{color: "var(--accent-metal)"}}><Sparkles size={11} className="inline mr-1"/>Create new code</div>
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Code</label>
            <Input data-testid="code-input" placeholder="FAMILY20" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="mt-1 uppercase"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">% off</label>
            <Input data-testid="pct-input" type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Max uses <span className="text-[9px]">(optional)</span></label>
            <Input data-testid="max-input" type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="unlimited" className="mt-1"/>
          </div>
          <div className="flex items-end">
            <Button data-testid="create-code-btn" onClick={create} disabled={busy} className="rounded-full w-full btn-primary"><Sparkles size={14} className="mr-2"/>Create</Button>
          </div>
          <div className="md:col-span-5">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Note to yourself <span className="text-[9px]">(optional)</span></label>
            <Textarea data-testid="note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Mom's holiday code"/>
          </div>
        </div>
      </div>

      {/* List */}
      {codes.length === 0 ? (
        <div data-testid="codes-empty" className="text-center p-10 border hairline rounded-lg bg-white text-[color:var(--text-muted)]">
          No discount codes yet. Create your first one above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="codes-list">
          {codes.map((c) => (
            <div key={c.code} data-testid={`code-row-${c.code}`} className="rounded-lg border hairline bg-white p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Tag size={14} style={{color: "var(--accent-metal)"}}/>
                  <span className="font-mono text-xl tracking-wider" style={{color: "var(--primary)"}}>{c.code}</span>
                </div>
                <Badge className="bg-amber-100 text-amber-900">{c.percent_off}% off</Badge>
                <Badge variant="outline">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""} used</Badge>
                {!c.active && <Badge variant="outline">Inactive</Badge>}
                {c.note && <span className="text-xs text-[color:var(--text-muted)] italic">{c.note}</span>}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`copy-${c.code}`} variant="outline" size="sm" onClick={() => copy(c.code)} className="rounded-full"><Copy size={12} className="mr-1"/>Copy</Button>
                <Button data-testid={`delete-${c.code}`} variant="ghost" size="sm" onClick={() => del(c.code)} className="rounded-full text-rose-700 hover:text-rose-900 hover:bg-rose-50"><Trash2 size={12} className="mr-1"/>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
